// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useMemo, createContext, useContext, useEffect } from 'react';
import { BrandingConfiguration, PossibleThemeName } from './theme.types';
import { getThemeNameFromLocalStorage, saveThemeNameToLocalStorage } from './themeLocalStorageUtils';
import { BRANDING_CONFIGURATIONS, getCurrentBrandingConfiguration } from './brandingConfigurations';
import { FluentThemeProvider } from '@azure/communication-react';
import { Snow } from 'components/Snow/Snow';
import { useExperiments } from 'experiments/ExperimentsProvider';

/**
 * Interface for React useContext hook containing the FluentTheme and a setter to switch themes
 */
interface SwitchableThemeContext {
  /**
   * Currently chosen theme.
   */
  currentThemeName: PossibleThemeName;

  /**
   * Currently chosen theme configuration.
   */
  currentBranding: BrandingConfiguration;

  toggleTheme: () => void;
}

const localStorageThemeName = getThemeNameFromLocalStorage();
const browserDefaultIsDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
const defaultThemeName = localStorageThemeName ?? (browserDefaultIsDarkTheme ? 'Dark' : 'Light');

/**
 * React useContext for FluentTheme state of SwitchableThemeProvider
 */
const SwitchableThemeContext = createContext<SwitchableThemeContext>({
  currentThemeName: defaultThemeName,
  currentBranding: BRANDING_CONFIGURATIONS.default,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  toggleTheme: () => {}
});

/**
 * Props for SwitchableThemeProvider
 */
export interface SwitchableThemeProviderProps {
  children: React.ReactNode;
}

/**
 * @description Provider wrapped around FluentThemeProvider that stores themes in local storage
 * to be switched via useContext hook.
 * @param props - SwitchableThemeProviderProps
 * @remarks This makes use of the browser's local storage if available
 */
export const SwitchableThemeProvider = (props: SwitchableThemeProviderProps): JSX.Element => {
  const { children } = props;

  const { experimentValues, updateExperimentValue } = useExperiments();
  const brandingEnabled = experimentValues.holidayThemes.activeValue;
  const currentBranding = brandingEnabled ? getCurrentBrandingConfiguration() : BRANDING_CONFIGURATIONS.default;

  const [currentThemeName, setCurrentThemeName] = useState<PossibleThemeName>(defaultThemeName);

  const state = useMemo<SwitchableThemeContext>(
    () => ({
      currentThemeName,
      brandingEnabled,
      currentBranding,
      toggleTheme: (): void => {
        const newThemeName = currentThemeName === 'Light' ? 'Dark' : 'Light';
        setCurrentThemeName(newThemeName);
        saveThemeNameToLocalStorage(newThemeName);
      },
      toggleBranding: (): void => {
        updateExperimentValue('holidayThemes', !brandingEnabled);
      }
    }),
    [currentThemeName, currentBranding, brandingEnabled]
  );

  useEffect(() => {
    /*
     * Workaround a div with no height that fluent adds to the DOM when using applyTo
     * More Info: https: //github.com/microsoft/fluentui/issues/21289#issuecomment-1014826490
     */
    const addCSS = (css): void => (document.head.appendChild(document.createElement('style')).innerHTML = css);
    addCSS(`
    #root>div {
      height: 100%;
    }
    `);
  }, []);

  const activeTheme =
    currentThemeName === 'Light' ? currentBranding.fluentTheme.Light : currentBranding.fluentTheme.Dark;

  const shouldApplyLightThemeSnowBackground =
    currentThemeName === 'Light' && currentBranding.landingPageEffect === 'snow';

  useEffect(() => {
    // Apply background to the element that fluent adds to the DOM to ensure it spans the whole page.
    if (shouldApplyLightThemeSnowBackground) {
      const div = document.getElementById('root')?.firstChild as HTMLDivElement;
      div.classList.add('snowBg');
    }

    return (): void => {
      const div = document.getElementById('root')?.firstChild as HTMLDivElement;
      div.classList.remove('snowBg');
    };
  }, [shouldApplyLightThemeSnowBackground]);

  return (
    <SwitchableThemeContext.Provider value={state}>
      <FluentThemeProvider fluentTheme={activeTheme}>
        <Snow />
        {children}
      </FluentThemeProvider>
    </SwitchableThemeContext.Provider>
  );
};

/**
 * React hook for programmatically accessing the switchable fluent theme name.
 */
export const useThemeName = (): PossibleThemeName => useContext(SwitchableThemeContext).currentThemeName;

/**
 * React hook for programmatically accessing the switchable fluent theme name.
 */
export const useToggleTheme = (): (() => void) => useContext(SwitchableThemeContext).toggleTheme;

/**
 * React hook for programmatically accessing the active branding configuration.
 */
export const useBranding = (): BrandingConfiguration => useContext(SwitchableThemeContext).currentBranding;

/**
 * React hook for retrieving the style to apply to anything that should render above the winter snow effect.
 */
export const useAboveSnowStyle = (): React.CSSProperties => {
  const { landingPageEffect } = useBranding();
  const result: React.CSSProperties = useMemo(
    () =>
      landingPageEffect === 'snow'
        ? {
            zIndex: 100, // must be greater than the z-index specified in app.css
            position: 'relative'
          }
        : {},
    [landingPageEffect]
  );

  return result;
};
